import React, { useCallback, useMemo, useState } from "react";
import { useReactFlow, useStore } from "reactflow";
import ModalShell from "../../ModalShell";
import {
  packagesApi,
  triggerBlobDownload,
  dependencyFailureNotice,
  renameModelCalls,
  withRestartNotice,
} from "../../../services/packages";
import { getPaletteNodeTypes } from "../../../registry";
import { useStarterContext } from "../../../providers/StarterProvider";
import { useToastContext } from "../../../providers/ToastProvider";
import { useFlowContext } from "../../../providers/FlowProvider";
import { installDraftToProject } from "../../../providers/packages/installDraftToProject";
import { hostedGuestSignInNote, useHostedGuest } from "../../apiSettings/useHostedGuest";
import { PackageTargetPicker, useWritablePackageOptions } from "./PackageTargetPicker";
import {
  SAVE_AS_NEW_PACK,
  buildFactoryInstallEnvelope,
  buildSaveAsInstallDraft,
  canvasTemplateLabelFromNode,
  normalizeTemplateLabel,
  runtimeCodeFromRfNode,
  saveAsWouldReplaceByLabel,
} from "../../../utils/palettePackageFactoryDraft";
import { getFlowNodeCanonicalType } from "../../../utils/flowNodeCanonicalType";
import { tryGetNodeDescriptor } from "../../../registry/nodeRegistry";
import { NodeTemplateId } from "../../../registry/types";
import styles from "./NodeSaveAsModal.module.css";

function packageLabelsForSectionKey(sectionKey: string): string[] {
  return getPaletteNodeTypes()
    .filter(
      (d) =>
        d.source === "package" &&
        d.package &&
        `${d.package.packageId}@${d.package.major}` === sectionKey,
    )
    .map((d) => d.label);
}

export function NodeSaveAsModal({
  show,
  nodeId,
  onClose,
}: {
  show: boolean;
  nodeId: string;
  onClose: () => void;
}) {
  const { setNodes } = useReactFlow();
  const { getStarters } = useStarterContext();
  const { showToast } = useToastContext();
  const { ensureProjectId } = useFlowContext();
  const hostedGuest = useHostedGuest();
  const [busy, setBusy] = useState(false);
  const [busyKind, setBusyKind] = useState<"save" | "export" | null>(null);

  const { options: packageOptions, registryKey } = useWritablePackageOptions();

  // Subscribed, not sampled. The only way in here is the Node settings modal's
  // "Save as package node...", whose onSave (styles.tsx) calls updateDataNode
  // and setSaveAsOpen(true) in one batch. updateDataNode writes FlowProvider's
  // useNodesState array, which reaches React Flow's store only when its
  // prop-sync effect runs, i.e. after the render in which `show` flips true. A
  // useMemo keyed on [show, nodeId, getNodes] therefore captured the *pre-edit*
  // node and, since none of those deps ever change again, held it for the
  // modal's whole lifetime: every edit just made in Node settings was dropped
  // from the saved package. Selecting off the store fixes that and keeps the
  // draft honest if the node changes again while the modal is open.
  //
  // The `show` guard matters for cost as much as correctness: this modal is
  // rendered once per canvas node, and returning a stable null for the closed
  // ones keeps them from re-rendering on every store change.
  const canvasNode = useStore(
    useCallback(
      (s: any) => (show ? s.nodeInternals.get(nodeId) ?? null : null),
      [show, nodeId],
    ),
  );

  const nodeLabel = useMemo(() => {
    if (!canvasNode) return "Node";
    const nt = getFlowNodeCanonicalType(canvasNode);
    if (!nt) return "Node";
    const desc = tryGetNodeDescriptor(nt as NodeTemplateId);
    if (!desc) return "Node";
    return canvasTemplateLabelFromNode(canvasNode, desc);
  }, [canvasNode]);

  // The defaults (the first writable package, "<node label> package") follow
  // the node and the package list until the person changes the destination or
  // the name; then both stay as they were. The package list follows the node
  // registry, which grows while packages load, and re-applying the defaults on
  // every change threw away a destination just chosen and a name being typed.
  //
  // The defaults are worked out in the render, so the modal is drawn with them.
  // An effect used to set them, and React renders the state an effect sets in
  // a task of its own: the modal was drawn first with an empty name, and on a
  // busy page the default could land while a person typed into that field,
  // putting what they typed after it ("Python Computation packageSaved
  // Roundtrip Package").
  //
  // Each opening starts over: what the person chose is dropped in the render in
  // which `show` changes, before anything is drawn.
  const [chosen, setChosen] = useState<{ targetKey: string; name: string } | null>(null);
  const [shownBefore, setShownBefore] = useState(show);
  if (shownBefore !== show) {
    setShownBefore(show);
    setChosen(null);
  }
  const defaultTargetKey = packageOptions[0]?.sectionKey ?? SAVE_AS_NEW_PACK;
  const defaultName = `${nodeLabel} package`;
  const targetKey = chosen?.targetKey ?? defaultTargetKey;
  const newPackageName = chosen?.name ?? defaultName;

  const chooseTarget = useCallback(
    (key: string) => setChosen((was) => ({ targetKey: key, name: was?.name ?? defaultName })),
    [defaultName],
  );

  const typeName = useCallback(
    (name: string) => setChosen((was) => ({ targetKey: was?.targetKey ?? defaultTargetKey, name })),
    [defaultTargetKey],
  );

  const willReplace = useMemo(() => {
    if (targetKey === SAVE_AS_NEW_PACK) return false;
    const labels = packageLabelsForSectionKey(targetKey);
    const norm = normalizeTemplateLabel(nodeLabel);
    return labels.some((l) => normalizeTemplateLabel(l) === norm);
  }, [targetKey, nodeLabel, registryKey]);

  const targetPackageName = useMemo(
    () => packageOptions.find((p) => p.sectionKey === targetKey)?.displayName,
    [packageOptions, targetKey],
  );

  // Shared by Save and Export: resolve the target package and turn the canvas
  // node into a factory draft. Returns null (after toasting) when the draft
  // can't be built, so both callers can simply bail.
  const buildDraft = useCallback(async () => {
    if (!canvasNode) return null;

    let draft;
    let replace = false;
    let replacedExistingKind = false;

    if (targetKey === SAVE_AS_NEW_PACK) {
      draft = buildSaveAsInstallDraft({
        canvasNode,
        target: { kind: "new", packageDisplayName: newPackageName.trim() || undefined },
        getStarters,
      });
    } else {
      const { packages } = await packagesApi.listInstalled();
      const pkg = packages.find((p) => `${p.packageId}@${p.major}` === targetKey);
      if (!pkg) {
        showToast("Could not load the selected package.", "warning");
        return null;
      }
      replacedExistingKind = saveAsWouldReplaceByLabel(pkg, nodeLabel);
      draft = buildSaveAsInstallDraft({
        canvasNode,
        target: { kind: "installed", package: pkg },
        getStarters,
      });
      replace = true;
    }

    if (!draft) {
      showToast("Could not build a package draft from this node.", "error");
      return null;
    }
    return { draft, replace, replacedExistingKind };
  }, [canvasNode, getStarters, newPackageName, nodeLabel, showToast, targetKey]);

  const onConfirm = useCallback(async () => {
    if (!canvasNode || busy) return;
    setBusy(true);
    setBusyKind("save");
    try {
      // The package goes into this dataflow, which a hosted guest that never
      // saved it has no id for, and cannot save: it is told to sign in before
      // anything is made.
      if (hostedGuest && !(await ensureProjectId())) {
        showToast(hostedGuestSignInNote("save nodes as packages"), "warning");
        return;
      }
      const built = await buildDraft();
      if (!built) return;
      const { draft, replace, replacedExistingKind } = built;

      const result = await installDraftToProject(buildFactoryInstallEnvelope(draft, replace), ensureProjectId);
      // The build DERIVES dependencies.python from this node's source, so a
      // body containing `import rasterio` produces a rasterio declaration.
      // Until the install started honouring it, "Save and install" could
      // report success over a package whose very first run raises.
      const depNotice = dependencyFailureNotice(`Saved ${nodeLabel}`, result);
      // Rebind the canvas node to the new/updated kind so re-opening Settings
      // resolves to the new descriptor (e.g. its readOnly flag), not the
      // source built-in. Match by label — Save-As preserves it. Also re-seed
      // `data.code` / `data.defaultCode` with the body we just persisted, so the
      // editor remount picks them up instead of clobbering them with the old
      // source descriptor's starter (or an empty initial state).
      const matchNorm = normalizeTemplateLabel(nodeLabel);
      const newKind = result.package.templates.find((k) => normalizeTemplateLabel(k.label) === matchNorm);
      if (newKind) {
        // A node that loads a model was saved as a model package of its own,
        // a copy of that model: the node runs the copy, named by the new id,
        // as the package's node does.
        const copied = result.copiedModel;
        const savedBody = copied
          ? renameModelCalls(runtimeCodeFromRfNode(canvasNode), copied, result.package.packageId)
          : runtimeCodeFromRfNode(canvasNode);
        setNodes((nodes) =>
          nodes.map((n) =>
            String(n.id) === nodeId
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    nodeType: newKind.id,
                    code: savedBody,
                    defaultCode: savedBody,
                    ...(copied ? { modelRefs: [{ id: result.package.packageId, name: result.package.name }] } : {}),
                  },
                }
              : n,
          ),
        );
      }
      // ONE verdict for one save. The dependency notice already says the save
      // happened ("Saved <node>, but ..."), so pairing it with a green
      // "Added ..." tells the user it worked and then that it did not - and
      // now that an error toast persists, both sit on screen together.
      showToast(
        withRestartNotice(
          depNotice ??
            (replacedExistingKind
              ? `Replaced "${nodeLabel}" in the package.`
              : `Added "${nodeLabel}" as a new kind in the package.`),
          result.restartRecommended,
        ),
        depNotice ? "error" : "success",
      );
      onClose();
    } catch (err) {
      showToast((err as Error)?.message ?? "Save As failed.", "error");
    } finally {
      setBusy(false);
      setBusyKind(null);
    }
  }, [buildDraft, busy, canvasNode, ensureProjectId, hostedGuest, nodeId, nodeLabel, onClose, setNodes, showToast, targetKey]);

  // Export the same draft as a .curio.zip without installing it. Shares the
  // `busy` flag with Save so the two can never run concurrently, and leaves
  // the modal open - an export is not a commit.
  //
  // Deliberately sends the *same* envelope Save would: `/factory/build` reads
  // only manifest/sources/readme/license and ignores `replace`. Both endpoints
  // run preserve_unedited_sources, so the downloaded zip carries the same
  // template bodies Save would have installed - including the real source of
  // siblings this draft only has placeholders for. Not literally byte-identical:
  // each request stamps its own `createdAt` when the draft omits one.
  const onExport = useCallback(async () => {
    if (!canvasNode || busy) return;
    setBusy(true);
    setBusyKind("export");
    try {
      const built = await buildDraft();
      if (!built) return;
      const { blob, filename } = await packagesApi.factoryBuild(
        buildFactoryInstallEnvelope(built.draft, built.replace),
      );
      triggerBlobDownload(blob, filename);
      showToast(`Exported ${filename}.`, "success");
    } catch (err) {
      showToast((err as Error)?.message ?? "Export failed.", "error");
    } finally {
      setBusy(false);
      setBusyKind(null);
    }
  }, [buildDraft, busy, canvasNode, showToast]);

  if (!show) return null;

  return (
    <ModalShell preservePackagePaletteOpen onClose={busy ? () => {} : onClose} titleId="save-as-package-title">
      <div className={styles.content}>
        <h2 id="save-as-package-title" className={styles.title}>Save as package node</h2>
        <p className={styles.subtitle}>
          Save <strong>{nodeLabel}</strong> into an installed package or create a new one.
        </p>

        <PackageTargetPicker
          idPrefix="save-as"
          options={packageOptions}
          targetKey={targetKey}
          onTargetKey={chooseTarget}
          newPackageName={newPackageName}
          onNewPackageName={typeName}
          busy={busy}
        >
          {willReplace ? (
            <p className={styles.warning} role="alert">
              <strong>Replace existing node.</strong> &quot;{nodeLabel}&quot; already exists in{" "}
              {targetPackageName ?? "this package"}. Saving will replace that kind&apos;s template and
              settings with this canvas node.
            </p>
          ) : (
            <p className={styles.hint}>Adds this node as a new kind in the selected package.</p>
          )}
        </PackageTargetPicker>

        <div className={styles.footer}>
          <button type="button" className={styles.ghostBtn} disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.ghostBtn}
            disabled={busy}
            data-tooltip="Download this node as a .curio.zip package without installing it"
            onClick={() => void onExport()}
          >
            {busyKind === "export" ? "Exporting…" : "Export"}
          </button>
          <button type="button" className={styles.primaryBtn} disabled={busy} onClick={() => void onConfirm()}>
            {busyKind === "save" ? "Saving…" : willReplace ? "Replace" : "Save"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

